import React, { useMemo, useState } from 'react';
import cx from 'classnames';

import {
    BroadcastVsDigitalFilter,
    ChartInfoTooltip,
    ChartOrEmptyMessage,
    Dropdown,
    PerformanceBarPerContributionType,
    PerformancePiePerContributionType,
} from '@trqk/components';
import { constants } from '@trqk/utils';

import { chartTypes } from '../../constants';

import { getChartConfigs } from './helpers';

const { broadcastVsDigitalOptions, broadcastOption } = constants;
const defaultOptionIndex = 0;
const chartHeight = 415;
const maxSize = 10;
const minValue = 0.01;

/**
 * @typedef {PerformanceChartsProps}
 * @property {object} trackData a ProTrack
 * @property {boolean} truncateChartData whether to filter and slice chart data
 */
interface PerformanceChartsProps {
    trackData: ProTrack;
    truncateChartData: boolean;
}

/**
 * Displays one of three available charts for a track's performance data, with the option to change
 * the chart type. Data is filtered by the music_user_genre_name, which the user can set to be
 * either 'Broadcast / Other' (treated as `'broadcast'` in terms of the value type) or 'Digital'
 * (`'digital'`).
 * @param {PerformanceChartsProps}
 * @returns rendered chart and associated dropdown and filter
 */
export const PerformanceCharts = ({
    trackData,
    truncateChartData,
}: PerformanceChartsProps): JSX.Element => {
    const chartConfigs = useMemo(
        () =>
            getChartConfigs({
                trackData,
                maxSize,
                minValue,
                truncateChartData,
            }),
        [trackData, truncateChartData],
    );
    const orderedChartConfigs = Array.from(chartConfigs.values());

    const dropdownOptions = orderedChartConfigs.map(
        ({
            dropdownOption,
        }: PerformanceChartConfig): DropdownOption<PerformanceChartType> =>
            dropdownOption,
    );
    const [chartType, setChartType] = useState<PerformanceChartType>(
        dropdownOptions[defaultOptionIndex].value,
    );

    const defaultFilterOption =
        broadcastVsDigitalOptions.get(
            (orderedChartConfigs[defaultOptionIndex].filterData.defaultFilter ??
                'broadcast') as BroadcastOrDigital,
        ) ?? broadcastOption;
    const [filterOption, setFilterOption] =
        useState<DropdownOption<BroadcastOrDigital>>(defaultFilterOption);

    const showEarningsData =
        chartConfigs.get(chartTypes.showEarnings)?.filterData.filteredData[
            filterOption.value
        ].data ?? ([] as ProShowNameContribution[]);
    const sourceEarningsData =
        chartConfigs.get(chartTypes.sourceEarnings)?.filterData.filteredData[
            filterOption.value
        ].data ?? ([] as ProSourceNameContribution[]);
    const sourcePlaysData =
        chartConfigs.get(chartTypes.sourcePlays)?.filterData.filteredData[
            filterOption.value
        ].data ?? ([] as ProSourceNameContribution[]);

    // Charts are shown and hidden with CSS because it helps performance. Otherwise, we have to set
    // up and tear down the chart components each time we show or hide them.
    return (
        <>
            <div className="flex justify-between items-end">
                <div className="ml-2 text-sm text-tertiary-400">
                    Select chart:
                </div>
                <div>
                    <BroadcastVsDigitalFilter
                        filterOption={filterOption}
                        setFilterOption={setFilterOption}
                    />
                </div>
            </div>
            <div className="mt-3">
                <div className="flex justify-between items-end">
                    <Dropdown
                        defaultIndex={defaultOptionIndex}
                        size="large"
                        options={dropdownOptions}
                        handleChange={(value: PerformanceChartType): void => {
                            setChartType(value);
                        }}
                        width={300}
                    />
                    {/*
                        maxSize is not used to filter sourceEarnings data
                    */}
                    {chartType !== chartTypes.sourceEarnings && (
                        <ChartInfoTooltip
                            maxSize={maxSize}
                            minValue={minValue}
                        />
                    )}
                </div>
            </div>
            {/* Earnings by Program */}
            <div
                className={cx({
                    hidden: chartType !== chartTypes.showEarnings,
                })}
            >
                <ChartOrEmptyMessage
                    data={showEarningsData}
                    height={chartHeight}
                >
                    <PerformanceBarPerContributionType
                        chartDivId="bar.showEarnings"
                        data={showEarningsData}
                        dataFields={{
                            category: 'show_name',
                            value: 'earnings_contribution',
                        }}
                        height={chartHeight}
                        numberFormat="$#,###.00"
                        tooltipLabels={{
                            category: 'Show',
                            value: 'Earnings',
                        }}
                    />
                </ChartOrEmptyMessage>
            </div>
            {/* Earnings Share by Source */}
            <div
                className={cx({
                    hidden: chartType !== chartTypes.sourceEarnings,
                })}
            >
                <ChartOrEmptyMessage
                    data={sourceEarningsData}
                    height={chartHeight}
                >
                    <PerformancePiePerContributionType
                        chartDivId="pie.sourceEarnings"
                        data={sourceEarningsData}
                        dataFields={{
                            value: 'earnings_contribution',
                            category: 'source_name',
                        }}
                        height={chartHeight}
                    />
                </ChartOrEmptyMessage>
            </div>
            {/* Plays by Source */}
            <div
                className={cx({
                    hidden: chartType !== chartTypes.sourcePlays,
                })}
            >
                <ChartOrEmptyMessage
                    data={sourcePlaysData}
                    height={chartHeight}
                >
                    <PerformanceBarPerContributionType
                        chartDivId="bar.sourcePlays"
                        data={sourcePlaysData}
                        dataFields={{
                            category: 'source_name',
                            value: 'plays_contribution',
                        }}
                        height={chartHeight}
                        tooltipLabels={{
                            category: 'Source',
                            value: 'Plays',
                        }}
                    />
                </ChartOrEmptyMessage>
            </div>
        </>
    );
};
